export interface AspectRatioBounds {
  min: number
  max: number
  errorMessage: string
  hint: string
}

export const IMAGE_ASPECT_RATIO_BOUNDS: AspectRatioBounds = {
  min: 1 / 2,
  max: 2,
  errorMessage:
    "This image is too wide or too tall. Please upload an image with an aspect ratio between 2:1 and 1:2.",
  hint: "Accepted aspect ratios: between 2:1 and 1:2.",
}

export const POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS: AspectRatioBounds = {
  min: 5 / 4,
  max: 4,
  errorMessage:
    "Primary Image/Video aspect ratio must be between 5:4 and 4:1.",
  hint: "Accepted aspect ratios: between 5:4 and 4:1.",
}

export const POST_PRIMARY_IMAGE_TOO_WIDE_ERROR =
  "Primary Image/Video is too wide. Please upload an image that is not wider than 4:1"

export const POST_PRIMARY_IMAGE_TOO_TALL_ERROR =
  "Primary Image/Video is too tall. Please upload an image that is not taller than 5:4"

export const IMAGE_ASPECT_RATIO_MIN = IMAGE_ASPECT_RATIO_BOUNDS.min
export const IMAGE_ASPECT_RATIO_MAX = IMAGE_ASPECT_RATIO_BOUNDS.max
export const IMAGE_ASPECT_RATIO_ERROR = IMAGE_ASPECT_RATIO_BOUNDS.errorMessage
export const IMAGE_ASPECT_RATIO_HINT = IMAGE_ASPECT_RATIO_BOUNDS.hint

export const POST_PRIMARY_IMAGE_ASPECT_RATIO_MIN =
  POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.min
export const POST_PRIMARY_IMAGE_ASPECT_RATIO_MAX =
  POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.max
export const POST_PRIMARY_IMAGE_ASPECT_RATIO_ERROR =
  POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.errorMessage
export const POST_PRIMARY_IMAGE_ASPECT_RATIO_HINT =
  POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.hint

export function isImageFile(file: File): boolean {
  return file.type.startsWith("image/")
}

export function getImageDimensions(
  file: File,
  errorMessage: string = IMAGE_ASPECT_RATIO_ERROR
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file)
    const image = new Image()

    image.onload = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({
        width: image.naturalWidth,
        height: image.naturalHeight,
      })
    }

    image.onerror = () => {
      console.debug("[ImageAspectRatio] Failed to read dimensions", {
        fileName: file.name,
        fileType: file.type,
      })
      URL.revokeObjectURL(objectUrl)
      reject(new Error(errorMessage))
    }

    image.src = objectUrl
  })
}

export async function validateImageAspectRatio(
  file: File,
  bounds: AspectRatioBounds = IMAGE_ASPECT_RATIO_BOUNDS
): Promise<void> {
  if (!isImageFile(file)) return

  const { width, height } = await getImageDimensions(file, bounds.errorMessage)
  if (!width || !height) {
    console.debug("[ImageAspectRatio] Missing dimensions", {
      fileName: file.name,
      fileType: file.type,
      width,
      height,
    })
    throw new Error(bounds.errorMessage)
  }

  const aspectRatio = width / height

  console.debug("[ImageAspectRatio] Computed", {
    fileName: file.name,
    fileType: file.type,
    width,
    height,
    aspectRatio,
    allowedMin: bounds.min,
    allowedMax: bounds.max,
  })

  if (aspectRatio < bounds.min || aspectRatio > bounds.max) {
    console.debug("[ImageAspectRatio] Rejected (out of range)", {
      fileName: file.name,
      fileType: file.type,
      aspectRatio,
    })
    throw new Error(bounds.errorMessage)
  }
}

export function postPrimaryAspectRatioError(aspectRatio: number): string {
  if (aspectRatio > POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.max) {
    return POST_PRIMARY_IMAGE_TOO_WIDE_ERROR
  }

  return POST_PRIMARY_IMAGE_TOO_TALL_ERROR
}

export async function validatePostPrimaryImageAspectRatio(
  file: File
): Promise<void> {
  if (!isImageFile(file)) return

  const { width, height } = await getImageDimensions(
    file,
    POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.errorMessage
  )
  if (!width || !height) {
    throw new Error(POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.errorMessage)
  }

  const aspectRatio = width / height

  console.debug("[ImageAspectRatio] Computed (post primary)", {
    fileName: file.name,
    fileType: file.type,
    width,
    height,
    aspectRatio,
    allowedMin: POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.min,
    allowedMax: POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.max,
  })

  if (
    aspectRatio < POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.min ||
    aspectRatio > POST_PRIMARY_IMAGE_ASPECT_RATIO_BOUNDS.max
  ) {
    console.debug("[ImageAspectRatio] Rejected (post primary)", {
      fileName: file.name,
      fileType: file.type,
      aspectRatio,
    })
    throw new Error(postPrimaryAspectRatioError(aspectRatio))
  }
}
